import React, {useState} from "react";

import {DishDetailsItem} from "@apps/dish/interfaces";
import cx from "classnames";

import {EventOrigin, recordEvent} from "@globals/config/eventUtils";
import getDefaultDishDetailsImageSizeGroup from "@globals/functions/getDefaultDishDetailsImageSizeGroup";
import {ImageSizeGroup} from "@globals/functions/imageUtils";
import formatCurrency from "@globals/utils/formatCurrency";

import CollapsibleSection from "@components/CollapsibleSection";
import FavoriteIcon from "@components/FavoriteIcon";
import ItemPurchaseButton from "@components/ItemPurchaseButton";
import MediaAlbumCarousel from "@components/MediaAlbumCarousel";
import MenuItemRibbon from "@components/MenuItemRibbon/MenuItemRibbon";
import Rating from "@components/Rating";
import RestaurantName from "@components/RestaurantName";
import SocialProofDetails from "@components/SocialProofDetails";
import {SocialProofDetailsViewType} from "@components/SocialProofDetails/interfaces";
import {InlineTagList} from "@components/Tags";

import styles from "./DishDetails.scss";
import DishItemNutrition from "./DishItemNutrition";
import DishReviewsContainer from "./DishReviewsContainer/DishReviewsContainer";
import SoldoutFavoritePrompt from "./SoldoutFavoritesPrompt/SoldoutFavoritePrompt";
import useDishDetailsConfig from "./hooks/useDishDetailsConfig";

type DishDetailsProps = {
  dish: DishDetailsItem;
  imageSize?: string;
  sizesAttribute?: string;
  imageSizeGroup?: ImageSizeGroup;
  mediaClassName?: string;
  contentClassName?: string;
  titleClassName?: string;
  openReviews?: boolean;
  hidePurchaseBtnOverride?: boolean;
  onPurchaseButtonClick?: () => void;
  eventOrigin?: EventOrigin;
};

const defaultDishDetailsImageSizeGroup = getDefaultDishDetailsImageSizeGroup();

export default function DishDetails({
  dish,
  imageSize,
  sizesAttribute,
  imageSizeGroup = defaultDishDetailsImageSizeGroup,
  mediaClassName,
  openReviews = false,
  contentClassName,
  titleClassName,
  hidePurchaseBtnOverride,
  onPurchaseButtonClick,
  eventOrigin = EventOrigin.QUICKVIEW,
}: DishDetailsProps): JSX.Element {
  const config = useDishDetailsConfig();
  const [displayReviews, setDisplayReviews] = useState(openReviews);

  const recordNutritionToggle = (isCollapsed: boolean) => {
    const msg = (isCollapsed ? "hide" : "show") + " nutrition info";
    recordEvent(eventOrigin, msg, dish.id);
  };

  const recordDescriptionToggle = (isCollapsed: boolean) => {
    const msg = (isCollapsed ? "hide" : "show") + " description";
    recordEvent(eventOrigin, msg, dish.id);
  };

  const showPrice = config.isPriceShown(dish) && !config.hidePriceInDishDetails;

  return (
    <div className={styles.container}>
      <MenuItemRibbon
        itemId={dish.item_id}
        almostGone={dish.almost_gone}
        className={dish.soldout && styles.soldout}
        isDishDetails={true}
      />
      <div
        className={cx(
          styles.media,
          mediaClassName,
          dish.soldout && styles.soldout
        )}
      >
        <MediaAlbumCarousel
          key={dish.id}
          itemId={dish.id}
          eventOrigin={EventOrigin.QUICKVIEW}
          keyPhoto={dish.photo}
          mediaAlbum={dish.media_album}
          imageSizeGroup={imageSizeGroup}
          size={imageSize}
          sizesAttribute={sizesAttribute}
        />
      </div>
      <div className={cx(styles.content, contentClassName)}>
        <h1 className={cx(styles.title, titleClassName)}>
          {dish.item}
          {config.showFavorites && (
            <FavoriteIcon
              className={styles.favoriteIcon}
              itemId={dish.item_id}
              eventOrigin={eventOrigin}
              showHoverBox={config.showFavoriteHoverBox}
            ></FavoriteIcon>
          )}
        </h1>

        <RestaurantName
          wrapperClassName={styles.restaurantLinkWrapper}
          restaurant={dish.restaurant}
          operatorSlug={dish.operator_slug}
          deliveryDate={config.selectedDate}
        />
        <SocialProofDetails
          itemId={dish.id}
          detailsType={SocialProofDetailsViewType.Explanation}
          className={styles.explanationContainer}
        />
        <div className={styles.doubleRow}>
          {showPrice && (
            <div className={styles.itemPrice}>
              {formatCurrency(Number(dish.price))}
            </div>
          )}
          {config.hideRating ||
            (dish.average_rating && (
              <Rating
                averageRating={dish.average_rating}
                reviewCount={dish.review_count}
                onClick={
                  config.showReviews ? () => setDisplayReviews(true) : undefined
                }
              />
            ))}
          <div className={styles.temperatureAndTagsRow}>
            {dish.temperature_display_string && (
              <span className={styles.temperature}>
                Served {dish.temperature_display_string}.
              </span>
            )}
            {dish.fixed_side_item?.temperature_display_string && (
              <span className={styles.sideTemperature}>
                Side served {dish.fixed_side_item.temperature_display_string}.
              </span>
            )}
            <InlineTagList tags={dish.tags} />
          </div>
        </div>
        <CollapsibleSection
          title="Dish Description"
          isCollapsed={false}
          onToggle={recordDescriptionToggle}
          sectionClassName={styles.description}
        >
          {dish.description}
        </CollapsibleSection>
        <CollapsibleSection
          title="Allergen & Nutrition information"
          onToggle={recordNutritionToggle}
        >
          <DishItemNutrition dish={dish} />
        </CollapsibleSection>
      </div>
      {(typeof hidePurchaseBtnOverride === "boolean"
        ? hidePurchaseBtnOverride
        : config.hidePurchaseButton) || (
        <div className={styles.detailsFooter}>
          <ItemPurchaseButton
            plusMinusClassName={styles.plusMinusClassName}
            item={dish}
            isBigButton
            postAddOneToCartCallback={onPurchaseButtonClick}
          />
          <SoldoutFavoritePrompt dish={dish} />
        </div>
      )}
      {/* REVIEWS */}
      {config.showReviews && dish.average_rating && (
        <DishReviewsContainer
          dish={dish}
          isOpen={displayReviews}
          onClose={() => setDisplayReviews(false)}
        />
      )}
    </div>
  );
}
